import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Alert,
  Button,
  Card,
  Loader,
  Input,
  Checkbox,
  Dialog,
  Title,
  Body,
  Subline,
  Icon,
  DescriptionList,
} from '../components/kern';

interface Project {
  id: number;
  name: string;
  api_key: string;
  allowed_origins: string;
  gitlab_project_id: string | null;
  gitlab_enabled: boolean;
  notify_email: string | null;
  email_enabled: boolean;
  is_active: boolean;
  feedback_count: number;
  created_at: string;
  updated_at: string;
}

interface ProjectFormData {
  name: string;
  allowed_origins: string;
  gitlab_project_id: string;
  gitlab_enabled: boolean;
  notify_email: string;
  email_enabled: boolean;
}

export function ProjectsPage() {
  const { token } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    allowed_origins: '',
    gitlab_project_id: '',
    gitlab_enabled: false,
    notify_email: '',
    email_enabled: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/projects', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Projekte');
      }

      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/v1/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Erstellen des Projekts');
      }

      const newProject = await response.json();
      setProjects([...projects, newProject]);
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProject) return;
    setIsSubmitting(true);

    try {
      const response = await fetch(`/api/v1/projects/${editingProject.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Aktualisieren des Projekts');
      }

      const updatedProject = await response.json();
      setProjects(projects.map((p) => (p.id === updatedProject.id ? updatedProject : p)));
      setEditingProject(null);
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async (project: Project) => {
    if (
      !confirm(
        `Möchtest du das Projekt "${project.name}" wirklich löschen? Alle zugehörigen Feedbacks werden ebenfalls gelöscht.`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/projects/${project.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen des Projekts');
      }

      setProjects(projects.filter((p) => p.id !== project.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    }
  };

  // TODO: Wire up to UI when needed
  // const handleToggleActive = async (project: Project) => {
  //   try {
  //     const response = await fetch(`/api/v1/projects/${project.id}`, {
  //       method: 'PATCH',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         Authorization: `Bearer ${token}`,
  //       },
  //       body: JSON.stringify({ is_active: !project.is_active }),
  //     });

  //     if (!response.ok) {
  //       throw new Error('Fehler beim Aktualisieren des Projekts');
  //     }

  //     setProjects(
  //       projects.map((p) => (p.id === project.id ? { ...p, is_active: !p.is_active } : p))
  //     );
  //   } catch (err) {
  //     setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
  //   }
  // };

  // const handleRegenerateApiKey = async (project: Project) => {
  //   if (
  //     !confirm('Möchtest du wirklich einen neuen API-Key generieren? Der alte Key wird ungültig.')
  //   ) {
  //     return;
  //   }

  //   try {
  //     const response = await fetch(`/api/v1/projects/${project.id}/regenerate-key`, {
  //       method: 'POST',
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });

  //     if (!response.ok) {
  //       throw new Error('Fehler beim Generieren des API-Keys');
  //     }

  //     const data = await response.json();
  //     setProjects(projects.map((p) => (p.id === project.id ? { ...p, api_key: data.api_key } : p)));
  //   } catch (err) {
  //     setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
  //   }
  // };

  const copyApiKey = (apiKey: string) => {
    navigator.clipboard.writeText(apiKey);
  };

  const openEditModal = (project: Project) => {
    setFormData({
      name: project.name,
      allowed_origins: project.allowed_origins,
      gitlab_project_id: project.gitlab_project_id || '',
      gitlab_enabled: project.gitlab_enabled,
      notify_email: project.notify_email || '',
      email_enabled: project.email_enabled,
    });
    setEditingProject(project);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      allowed_origins: '',
      gitlab_project_id: '',
      gitlab_enabled: false,
      notify_email: '',
      email_enabled: false,
    });
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <Loader />
        <Body>Lade Projekte...</Body>
      </div>
    );
  }

  return (
    <div className="projects-page">
      <header className="page-header">
        <div>
          <Title>Projekte</Title>
          <Subline>Verwalte deine Feedback-Projekte</Subline>
        </div>
        <br />
        <Button
          variant="primary"
          icon="add"
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
        >
          Neues Projekt
        </Button>
      </header>

      {error && (
        <Alert variant="danger" className="page-alert">
          {error}
        </Alert>
      )}

      {projects.length > 0 ? (
        <div className="kern-container">
          <div className="kern-row">
            {projects.map((project) => (
              <div key={project.id} className="kern-col-xl-6 kern-col-md-12">
                <Card
                  className={`project-card ${!project.is_active ? 'project-card--inactive' : ''}`}
                >
                  <header className="kern-card__header">
                    <div className="project-header" style={{ marginBottom: '16px' }}>
                      <Title as="h3" size="default">
                        {project.name}
                      </Title>
                    </div>

                    <div className="api-key-section">
                      <Body size="small" className="api-key-label">
                        API-Key:
                      </Body>
                      <div className="api-key-display">
                        <code className="api-key-code">{project.api_key.substring(0, 20)}...</code>
                        <Button
                          variant="tertiary"
                          onClick={() => copyApiKey(project.api_key)}
                          title="API-Key kopieren"
                        >
                          <Icon name="content-copy" size="small" />
                        </Button>
                      </div>
                    </div>

                    <DescriptionList
                      className="project-info"
                      items={[
                        { key: 'Origins', value: project.allowed_origins || '*' },
                        {
                          key: 'GitLab',
                          value: (
                            <span
                              className={`status-indicator ${project.gitlab_enabled ? 'status-indicator--enabled' : 'status-indicator--disabled'}`}
                            >
                              <Icon
                                name={project.gitlab_enabled ? 'check' : 'close'}
                                size="small"
                              />
                              {project.gitlab_enabled ? 'Aktiviert' : 'Deaktiviert'}
                              {project.gitlab_project_id && ` (${project.gitlab_project_id})`}
                            </span>
                          ),
                        },
                        {
                          key: 'E-Mail',
                          value: (
                            <span
                              className={`status-indicator ${project.email_enabled ? 'status-indicator--enabled' : 'status-indicator--disabled'}`}
                            >
                              <Icon name={project.email_enabled ? 'check' : 'close'} size="small" />
                              {project.email_enabled ? 'Aktiviert' : 'Deaktiviert'}
                              {project.notify_email && ` (${project.notify_email})`}
                            </span>
                          ),
                        },
                      ]}
                    />
                  </header>

                  <footer className="kern-card__footer">
                    <div className="project-actions">
                      <Button
                        style={{ marginTop: '16px' }}
                        variant="secondary"
                        icon="edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(project);
                        }}
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        style={{ marginTop: '16px' }}
                        variant="secondary"
                        icon="delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project);
                        }}
                        className="action-button--danger"
                      >
                        Löschen
                      </Button>
                    </div>
                  </footer>
                </Card>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <Icon name="folder" size="large" className="empty-state-icon" />
          <Title as="h3">Keine Projekte vorhanden</Title>
          <Body>Erstelle dein erstes Projekt, um Feedbacks zu sammeln.</Body>
          <Button
            variant="primary"
            icon="add"
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
          >
            Neues Projekt erstellen
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog
        open={showCreateModal || !!editingProject}
        onClose={() => {
          setShowCreateModal(false);
          setEditingProject(null);
        }}
        title={editingProject ? 'Projekt bearbeiten' : 'Neues Projekt'}
      >
        <form onSubmit={editingProject ? handleUpdateProject : handleCreateProject}>
          <div className="kern-dialog__body">
            <Input
              id="name"
              label="Projektname"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Mein Projekt"
            />

            <Input
              id="allowed_origins"
              label="Erlaubte Origins"
              value={formData.allowed_origins}
              onChange={(e) => setFormData({ ...formData, allowed_origins: e.target.value })}
              placeholder="https://example.com, https://app.example.com"
              hint="Komma-getrennte Liste. Leer lassen für alle Origins (*)."
            />

            <Input
              id="gitlab_project_id"
              label="GitLab Projekt-ID"
              value={formData.gitlab_project_id}
              onChange={(e) => setFormData({ ...formData, gitlab_project_id: e.target.value })}
              placeholder="123456"
              hint="Für automatische Issue-Erstellung in GitLab."
            />

            <Checkbox
              id="gitlab_enabled"
              label="GitLab-Integration aktiviert"
              checked={formData.gitlab_enabled}
              onChange={(e) => setFormData({ ...formData, gitlab_enabled: e.target.checked })}
            />

            <Input
              id="notify_email"
              label="Benachrichtigungs-E-Mail"
              type="email"
              value={formData.notify_email}
              onChange={(e) => setFormData({ ...formData, notify_email: e.target.value })}
              placeholder="team@example.com"
              hint="Komma-getrennt für mehrere Empfänger."
            />

            <Checkbox
              id="email_enabled"
              label="E-Mail-Benachrichtigung aktiviert"
              checked={formData.email_enabled}
              onChange={(e) => setFormData({ ...formData, email_enabled: e.target.checked })}
            />
          </div>

          <footer className="kern-dialog__footer">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setEditingProject(null);
              }}
            >
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Speichern...' : editingProject ? 'Speichern' : 'Projekt erstellen'}
            </Button>
          </footer>
        </form>
      </Dialog>
    </div>
  );
}
